import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { extractGraphQLError } from '@/utils/graphqlError';
import type { MutationResponse } from '@/types/composition-functions';
import {
  ADD_STUDENT_COURSE_TO_EXEMPT,
  UPDATE_STUDENT_COURSE_EXEMPTION,
  BULK_UPDATE_STUDENT_COURSE_EXEMPTION,
  BULK_DELETE_STUDENT_COURSE_EXEMPTION,
  BULK_EXEMPT_BY_STUDENTS,
  BULK_EXEMPT,
  BULK_EXEMPT_FROM_EXCEL,
} from '@/graphql/mutations/courseExemption';

export function useCourseExemptionMutations() {
  const toast = useToast();

  const addMutation = useMutation<{ addStudentCourseToExempt: MutationResponse }>(ADD_STUDENT_COURSE_TO_EXEMPT);
  const updateMutation = useMutation<{ updateStudentCourseExemption: MutationResponse }>(UPDATE_STUDENT_COURSE_EXEMPTION);
  const bulkUpdateMutation = useMutation<{ bulkUpdateStudentCourseExemption: MutationResponse }>(BULK_UPDATE_STUDENT_COURSE_EXEMPTION);
  const bulkDeleteMutation = useMutation<{ bulkDeleteStudentCourseExemption: MutationResponse }>(BULK_DELETE_STUDENT_COURSE_EXEMPTION);
  const bulkByStudentsMutation = useMutation<{ bulkExemptByStudents: MutationResponse }>(BULK_EXEMPT_BY_STUDENTS);
  const bulkExemptMutation = useMutation<{ bulkExempt: MutationResponse }>(BULK_EXEMPT);
  const bulkFromExcelMutation = useMutation<{ bulkExemptFromExcel: MutationResponse }>(BULK_EXEMPT_FROM_EXCEL);

  // Backend failures often arrive as `success: false` on a 200 response (e.g.
  // "already exempted") rather than as a thrown GraphQL error, so the failure
  // branch needs its own toast — otherwise the UI shows nothing at all.
  function notifyResult(result: MutationResponse | null | undefined, successFallback: string, failureFallback: string) {
    if (result?.success) {
      toast.add({ severity: 'success', summary: 'Success', detail: result.message || successFallback, life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: result?.message || failureFallback, life: 3000 });
    }
  }

  async function addStudentCourseToExempt(input: { student_id: string; course_id: string }) {
    try {
      const { data } = (await addMutation.mutate({ input }))!;
      const result = data?.addStudentCourseToExempt;
      notifyResult(result, 'Exemption added', 'The exemption could not be added.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error adding exemption'), life: 3000 });
      return null;
    }
  }

  async function updateStudentCourseExemption(id: string, fields: { course_id?: string; deleted?: number }) {
    try {
      const { data } = (await updateMutation.mutate({ id, fields }))!;
      const result = data?.updateStudentCourseExemption;
      notifyResult(result, 'Exemption updated', 'The exemption could not be updated.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating exemption'), life: 3000 });
      return null;
    }
  }

  async function bulkUpdateStudentCourseExemption(input: Array<{ id: string; course_id?: string; deleted?: number }>) {
    try {
      const { data } = (await bulkUpdateMutation.mutate({ input }))!;
      const result = data?.bulkUpdateStudentCourseExemption;
      notifyResult(result, 'Exemptions updated', 'The exemptions could not be updated.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error updating exemptions'), life: 3000 });
      return null;
    }
  }

  async function bulkDeleteStudentCourseExemption(ids: string[]) {
    try {
      const { data } = (await bulkDeleteMutation.mutate({ ids }))!;
      const result = data?.bulkDeleteStudentCourseExemption;
      notifyResult(result, 'Exemptions deleted', 'The exemptions could not be deleted.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error deleting exemptions'), life: 3000 });
      return null;
    }
  }

  async function bulkExemptByStudents(input: { student_ids: string[]; course_ids: string[]; deleted?: number }) {
    try {
      const { data } = (await bulkByStudentsMutation.mutate({ input }))!;
      const result = data?.bulkExemptByStudents;
      notifyResult(result, 'Bulk exemption applied', 'The bulk exemption could not be applied.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error applying bulk exemption'), life: 3000 });
      return null;
    }
  }

  async function bulkExempt(input: { scope_type: string; scope_id: string; levels: string[]; course_ids: string[]; deleted?: number }) {
    try {
      const { data } = (await bulkExemptMutation.mutate({ input }))!;
      const result = data?.bulkExempt;
      notifyResult(result, 'Bulk exemption applied', 'The bulk exemption could not be applied.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error applying bulk exemption'), life: 3000 });
      return null;
    }
  }

  async function bulkExemptFromExcel(matricList: File, courseIds: string[]) {
    try {
      const { data } = (await bulkFromExcelMutation.mutate({ matric_list: matricList, course_ids: courseIds }))!;
      const result = data?.bulkExemptFromExcel;
      notifyResult(result, 'Exemptions imported from file', 'The exemptions could not be imported.');
      return result;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Error importing exemptions'), life: 3000 });
      return null;
    }
  }

  return {
    addStudentCourseToExempt,
    updateStudentCourseExemption,
    bulkUpdateStudentCourseExemption,
    bulkDeleteStudentCourseExemption,
    bulkExemptByStudents,
    bulkExempt,
    bulkExemptFromExcel,

    addingExemption: addMutation.loading,
    updatingExemption: updateMutation.loading,
    bulkUpdating: bulkUpdateMutation.loading,
    bulkDeleting: bulkDeleteMutation.loading,
    bulkExemptingByStudents: bulkByStudentsMutation.loading,
    bulkExempting: bulkExemptMutation.loading,
    bulkExemptingFromExcel: bulkFromExcelMutation.loading,
  };
}
